import { LoadableInterface } from 'shared/types/loadableInterface';
import { makeAutoObservable, reaction } from 'mobx';
import { ExplorerEntityTypes } from 'entities/types';
import { FileModel } from 'entities/file/model/fileModel';
import { FolderModel } from 'entities/folder/model/folderModel';
import { SectionModel } from 'entities/section/model/sectionModel';
import { TableModel } from 'entities/table/model/tableModel';
import { RootStore } from 'app/store/rootStore';

export type SomeEntity =
  | { type: ExplorerEntityTypes.SECTION; model: SectionModel }
  | { type: ExplorerEntityTypes.FOLDER; model: FolderModel }
  | { type: ExplorerEntityTypes.FILE; model: FileModel }
  | { type: ExplorerEntityTypes.TABLE; model: TableModel };

export interface ExplorerInterface extends LoadableInterface {
  isPending: boolean;
  isError: boolean;
  currentEntity: SomeEntity | null;
  setCurrentEntity: (
    id: string | null,
    type: ExplorerEntityTypes | null
  ) => void;
}

export class ExplorerStore implements ExplorerInterface {
  private _rootStore: RootStore;

  constructor({
    rootStore,
    isDisableReaction,
  }: {
    rootStore: RootStore;
    isDisableReaction?: boolean;
  }) {
    this._rootStore = rootStore;
    makeAutoObservable(this);

    if (isDisableReaction) {
      return;
    }

    reaction(
      () => this.isPending,
      (isPending) => {
        this._rootStore.stateStore.isExplorerLoading = isPending;
      }
    );
  }

  public get isPending() {
    return Boolean(this._currentEntity?.model.isPending);
  }

  public get isError() {
    return Boolean(this._currentEntity?.model.isError);
  }

  public _currentEntity: SomeEntity | null = null;
  public get currentEntity(): SomeEntity | null {
    if (this._currentEntity?.type === ExplorerEntityTypes.SECTION) {
      const existingModel =
        this._rootStore?.sectionStore?.findSectionById(
          this._currentEntity.model.id
        ) || null;

      if (existingModel === null) {
        return null;
      }

      return { type: ExplorerEntityTypes.SECTION, model: existingModel };
    }

    return this._currentEntity;
  }

  public setCurrentEntity(id: string | null, type: ExplorerEntityTypes | null) {
    if (id === null) {
      this._currentEntity = null;
      return;
    }

    if (type === null) {
      throw new Error('Нельзя передавать id без type');
    }

    if (
      this._currentEntity?.model.id === id &&
      this._currentEntity?.type === type
    ) {
      return;
    }

    switch (type) {
      case ExplorerEntityTypes.SECTION:
        this._currentEntity = {
          type: ExplorerEntityTypes.SECTION,
          model: new SectionModel(id),
        };
        break;
      case ExplorerEntityTypes.FOLDER:
        this._currentEntity = {
          type: ExplorerEntityTypes.FOLDER,
          model: new FolderModel(id),
        };
        break;
      case ExplorerEntityTypes.FILE:
        this._currentEntity = {
          type: ExplorerEntityTypes.FILE,
          model: new FileModel(id),
        };
        break;
      case ExplorerEntityTypes.TABLE:
        this._currentEntity = {
          type: ExplorerEntityTypes.TABLE,
          model: new TableModel(id),
        };
        break;
    }
  }

  public clearCurrentEntity() {
    this._currentEntity = null;
  }
}
